import React from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import AppButton from '../AppButton/AppButton';
import { COLORS, FONT_SIZES, FONT_WEIGHTS } from '../../theme/constants';

/**
 * Validation / info dialog (MUI). Replaces Carbon InfoDialogBox.
 * messageInfo: API error body — string, or { message, errors: [] }, or arbitrary object (title falls back).
 */
const InfoDialog = ({ openDialog, setOpenDialog, messageInfo, type }) => {
  const title =
    typeof messageInfo === 'string'
      ? messageInfo
      : messageInfo?.message ||
        messageInfo?.detail ||
        (typeof messageInfo === 'object' && messageInfo !== null
          ? 'Validation error'
          : 'Details');

  const errors = Array.isArray(messageInfo?.errors) ? messageInfo.errors : null;
  const detail = messageInfo?.detail;

  const handleClose = () => setOpenDialog(false);

  return (
    <Dialog
      open={openDialog}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      data-testid="info"
      PaperProps={{
        sx: {
          borderRadius: 2,
          border: `1px solid ${alpha(COLORS.border.main, 0.2)}`,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 2,
          pr: 1,
          borderBottom: 1,
          borderColor: 'divider',
          py: 2,
        }}
      >
        <Typography
          variant="h6"
          component="span"
          sx={{
            fontWeight: FONT_WEIGHTS.semiBold,
            fontSize: FONT_SIZES.h5,
            color: type === 'error' ? COLORS.error.main : COLORS.text.primary,
          }}
        >
          {title}
        </Typography>
        <IconButton aria-label="Close" size="small" onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {detail && (
          <Typography
            component="div"
            sx={{
              mb: errors?.length ? 2 : 0,
              color: COLORS.text.primary,
              fontSize: FONT_SIZES.body2,
              lineHeight: 1.6,
            }}
          >
            {detail}
          </Typography>
        )}
        {errors && errors.length > 0 && (
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{
              border: `1px solid ${COLORS.border.light}`,
              borderRadius: 1,
              maxHeight: 360,
            }}
          >
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      fontWeight: FONT_WEIGHTS.semiBold,
                      fontSize: FONT_SIZES.tableHeader,
                      backgroundColor: COLORS.background.default,
                      color: COLORS.text.secondary,
                    }}
                  >
                    List of errors
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {errors.map((error, index) => (
                  <TableRow key={index} hover>
                    <TableCell sx={{ fontSize: FONT_SIZES.body2, wordBreak: 'break-word' }}>
                      {typeof error === 'object' ? JSON.stringify(error) : String(error)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider' }}>
        <AppButton variantType="secondary" data-testid="close" onClick={handleClose}>
          Close
        </AppButton>
      </DialogActions>
    </Dialog>
  );
};

InfoDialog.propTypes = {
  openDialog: PropTypes.bool.isRequired,
  setOpenDialog: PropTypes.func.isRequired,
  messageInfo: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  type: PropTypes.oneOf(['error', 'info']),
};

InfoDialog.defaultProps = {
  messageInfo: {},
  type: 'error',
};

export default InfoDialog;
